import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Mic, Pause, PlayArrow, Stop } from '@mui/icons-material';

import { platformApi } from '../../services/platformApi';
import { useLanguage } from '../../LanguageContext';

const TARGET_SAMPLE_RATE = 16000;

export interface AiInterviewConfig {
  language: string;
  interviewee_gender?: string;
  goal?: string;
  outline?: string;
  voice_id?: string;
}

interface AiInterviewPanelProps {
  projectId: number;
  sessionId: number;
  intervieweeName: string;
  config: AiInterviewConfig;
  onCompleted: (result: { transcript_id?: number | null; artifact_id?: number | null }) => void;
}

type LiveStatus = 'idle' | 'connecting' | 'in_progress' | 'paused' | 'completed' | 'error';

interface LiveTurn {
  role: string;
  text: string;
}

const downsample = (input: Float32Array, sourceRate: number, targetRate: number): Float32Array => {
  if (sourceRate === targetRate) {
    return input;
  }
  const ratio = sourceRate / targetRate;
  const length = Math.floor(input.length / ratio);
  const result = new Float32Array(length);
  for (let i = 0; i < length; i += 1) {
    const start = Math.floor(i * ratio);
    const end = Math.min(Math.floor((i + 1) * ratio), input.length);
    let total = 0;
    for (let j = start; j < end; j += 1) {
      total += input[j];
    }
    result[i] = total / Math.max(end - start, 1);
  }
  return result;
};

const floatTo16BitPcm = (input: Float32Array, sourceRate: number): Uint8Array => {
  const resampled = downsample(input, sourceRate, TARGET_SAMPLE_RATE);
  const output = new Uint8Array(resampled.length * 2);
  for (let i = 0; i < resampled.length; i += 1) {
    const sample = Math.max(-1, Math.min(1, resampled[i]));
    const value = Math.round(sample < 0 ? sample * 0x8000 : sample * 0x7fff);
    output[i * 2] = value & 0xff;
    output[i * 2 + 1] = (value >> 8) & 0xff;
  }
  return output;
};

const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...Array.from(bytes.subarray(i, i + chunkSize)));
  }
  return btoa(binary);
};

const base64ToInt16 = (base64: string): Int16Array => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Int16Array(bytes.buffer, 0, Math.floor(bytes.length / 2));
};

export const AiInterviewPanel: React.FC<AiInterviewPanelProps> = ({
  projectId,
  sessionId,
  intervieweeName,
  config,
  onCompleted,
}) => {
  const { t } = useLanguage();
  const [status, setStatus] = useState<LiveStatus>('idle');
  const [turns, setTurns] = useState<LiveTurn[]>([]);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const micContextRef = useRef<AudioContext | null>(null);
  const playbackContextRef = useRef<AudioContext | null>(null);
  const playheadRef = useRef<number>(0);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const pausedRef = useRef<boolean>(false);

  const teardownAudio = useCallback(() => {
    processorRef.current?.disconnect();
    sourceRef.current?.disconnect();
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    micContextRef.current?.close().catch(() => undefined);
    processorRef.current = null;
    sourceRef.current = null;
    mediaStreamRef.current = null;
    micContextRef.current = null;
  }, []);

  const teardown = useCallback(() => {
    teardownAudio();
    if (wsRef.current && wsRef.current.readyState <= WebSocket.OPEN) {
      wsRef.current.close();
    }
    wsRef.current = null;
    playbackContextRef.current?.close().catch(() => undefined);
    playbackContextRef.current = null;
  }, [teardownAudio]);

  useEffect(() => teardown, [teardown]);

  const playAgentAudio = useCallback((int16: Int16Array) => {
    if (int16.length === 0) {
      return;
    }
    let context = playbackContextRef.current;
    if (!context) {
      const Ctx = window.AudioContext || (window as any).webkitAudioContext;
      context = new Ctx();
      playbackContextRef.current = context;
      playheadRef.current = context.currentTime;
    }
    const buffer = context.createBuffer(1, int16.length, TARGET_SAMPLE_RATE);
    const channel = buffer.getChannelData(0);
    for (let i = 0; i < int16.length; i += 1) {
      channel[i] = int16[i] / 0x8000;
    }
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);
    const startAt = Math.max(context.currentTime, playheadRef.current);
    source.start(startAt);
    playheadRef.current = startAt + buffer.duration;
  }, []);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      let message: any;
      try {
        message = JSON.parse(event.data);
      } catch {
        return;
      }
      switch (message.type) {
        case 'status':
          if (message.status === 'in_progress') {
            setStatus('in_progress');
          } else if (message.status === 'paused') {
            setStatus('paused');
          }
          break;
        case 'transcript':
          setTurns((current) => [...current, { role: message.role, text: message.text }]);
          break;
        case 'agent_audio':
          playAgentAudio(base64ToInt16(message.audio_base64));
          break;
        case 'completed':
          setStatus('completed');
          teardown();
          onCompleted({
            transcript_id: message.transcript_id,
            artifact_id: message.artifact_id,
          });
          break;
        case 'error':
          setError(message.message || 'Voice agent error');
          setStatus('error');
          break;
        default:
          break;
      }
    },
    [onCompleted, playAgentAudio, teardown]
  );

  const startMicCapture = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
    });
    mediaStreamRef.current = stream;
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    const context: AudioContext = new Ctx();
    micContextRef.current = context;
    const source = context.createMediaStreamSource(stream);
    sourceRef.current = source;
    const processor = context.createScriptProcessor(4096, 1, 1);
    processorRef.current = processor;
    processor.onaudioprocess = (audioEvent) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN || pausedRef.current) {
        return;
      }
      const input = audioEvent.inputBuffer.getChannelData(0);
      const pcm = floatTo16BitPcm(input, context.sampleRate);
      ws.send(
        JSON.stringify({
          type: 'audio',
          audio_base64: bytesToBase64(pcm),
          sample_rate_hz: TARGET_SAMPLE_RATE,
        })
      );
    };
    source.connect(processor);
    processor.connect(context.destination);
  }, []);

  const start = useCallback(async () => {
    setError(null);
    setTurns([]);
    setStatus('connecting');
    try {
      const url = platformApi.interviewStudioVoiceAgentStreamUrl(projectId, sessionId);
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onmessage = handleMessage;
      ws.onerror = () => {
        setError(t('couldNotConnectAiInterview'));
        setStatus('error');
      };
      ws.onclose = () => {
        setStatus((current) => (current === 'completed' ? current : 'idle'));
      };
      ws.onopen = async () => {
        ws.send(JSON.stringify({ type: 'start', config }));
        try {
          await startMicCapture();
        } catch {
          setError(t('couldNotAccessMicrophone'));
          setStatus('error');
        }
      };
    } catch {
      setError(t('couldNotConnectAiInterview'));
      setStatus('error');
    }
  }, [config, handleMessage, projectId, sessionId, startMicCapture, t]);

  const pause = useCallback(() => {
    pausedRef.current = true;
    wsRef.current?.send(JSON.stringify({ type: 'control', action: 'pause' }));
  }, []);

  const resume = useCallback(() => {
    pausedRef.current = false;
    wsRef.current?.send(JSON.stringify({ type: 'control', action: 'resume' }));
  }, []);

  const complete = useCallback(() => {
    pausedRef.current = true;
    teardownAudio();
    wsRef.current?.send(JSON.stringify({ type: 'control', action: 'complete' }));
  }, [teardownAudio]);

  const live = status === 'in_progress' || status === 'paused';

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          size="small"
          color={status === 'in_progress' ? 'success' : status === 'paused' ? 'warning' : 'default'}
          label={t(`aiInterviewStatus_${status}`)}
        />
        {status === 'idle' || status === 'error' || status === 'completed' ? (
          <Button variant="contained" startIcon={<Mic />} onClick={start}>
            {t('startAiInterview')}
          </Button>
        ) : null}
        {status === 'in_progress' && (
          <Button variant="outlined" startIcon={<Pause />} onClick={pause}>
            {t('pause')}
          </Button>
        )}
        {status === 'paused' && (
          <Button variant="outlined" startIcon={<PlayArrow />} onClick={resume}>
            {t('resume')}
          </Button>
        )}
        {live && (
          <Button variant="contained" color="error" startIcon={<Stop />} onClick={complete}>
            {t('completeInterview')}
          </Button>
        )}
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, maxHeight: 320, overflowY: 'auto' }}>
        {turns.length === 0 ? (
          <Typography color="text.secondary">{t('aiInterviewLiveHint')}</Typography>
        ) : (
          <Stack spacing={1}>
            {turns.map((turn, index) => (
              <Box key={index}>
                <Typography variant="caption" color="text.secondary">
                  {turn.role === 'interviewer' ? t('aiInterviewer') : intervieweeName}
                </Typography>
                <Typography>{turn.text}</Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
};
